// src/components/insurance/InsuranceForm.tsx
import React, {JSX} from 'react';
import { Save, X, RefreshCw } from 'lucide-react';
import { InsuranceData } from '../../pages/Insurance'; // Adjust path
import { LookupValue } from '../../services/lookupService'; // Adjust path
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface InsuranceFormProps {
  formData: InsuranceData;
  editingId: string | null;
  isFormSubmitted: boolean;
  dropdownOptions: {
    ownerType: LookupValue[];
    insuranceType: LookupValue[];
    insuranceSubType: LookupValue[];
    insuranceInstallmentFrequency: LookupValue[];
  };
  dependents: { id: string; name: string }[];
  loadingSubTypes: boolean;
  handleSubmit: (e: React.FormEvent) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onCancel: () => void;
  renderDropdownOptions: (options: LookupValue[], placeholder?: string) => JSX.Element;
}

const InsuranceForm: React.FC<InsuranceFormProps> = ({
  formData, editingId, isFormSubmitted, dropdownOptions, dependents, loadingSubTypes,
  handleSubmit, handleInputChange, handleSelectChange, onCancel, renderDropdownOptions
}) => {
  return (
    <div className="p-6 bg-white">
      <h3 className="text-xl font-semibold text-gray-900 mb-4 md:mb-6">{editingId ? 'Edit Insurance' : 'Add New Insurance'}</h3>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Owner Type *</label>
                <select name="insuranceOwnerType" value={formData.insuranceOwnerType} onChange={handleSelectChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required>
                    {renderDropdownOptions(dropdownOptions.ownerType, 'Select Owner Type')}
                </select>
            </div>
            {formData.insuranceOwnerType === '2' && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Family Member *</label>
                    <select name="familyMemberId" value={formData.familyMemberId} onChange={handleSelectChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required>
                        <option value="">Select Family Member</option>
                        {dependents.map((dep) => (<option key={dep.id} value={dep.id}>{dep.name}</option>))}
                    </select>
                </div>
            )}
            <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Policy Name *</label>
                <input type="text" name="insuranceName" value={formData.insuranceName} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required />
            </div>
            <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Type *</label>
                <select name="insuranceType" value={formData.insuranceType} onChange={handleSelectChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required>
                    {renderDropdownOptions(dropdownOptions.insuranceType, 'Select Type')}
                </select>
            </div>
            <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Sub Type</label>
                <select name="insuranceSubType" value={formData.insuranceSubType} onChange={handleSelectChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" disabled={loadingSubTypes}>
                    {loadingSubTypes ? <option>Loading...</option> : renderDropdownOptions(dropdownOptions.insuranceSubType, formData.insuranceType ? 'Select Sub Type' : 'Select Type first')}
                </select>
            </div>
            <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Installment Amount *</label>
                <input type="number" name="insuranceInstallmentAmount" value={formData.insuranceInstallmentAmount} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required />
            </div>
            <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
                <select name="insuranceInstallmentFrequency" value={formData.insuranceInstallmentFrequency} onChange={handleSelectChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm">
                    {renderDropdownOptions(dropdownOptions.insuranceInstallmentFrequency, 'Select Frequency')}
                </select>
            </div>
            <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Coverage Amount</label>
                <input type="number" name="totalInsuranceAmount" value={formData.totalInsuranceAmount} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                </label>

                <DatePicker
                    selected={formData.insuranceStartDate ? new Date(formData.insuranceStartDate) : null}
                    onChange={(date: Date | null) => {
                    handleInputChange({
                        target: {
                        name: 'insuranceStartDate',
                        value: date ? date.toISOString().split('T')[0] : ''
                        }
                    } as React.ChangeEvent<HTMLInputElement>);
                    }}
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select start date"
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={15}
                    maxDate={formData.insuranceEndDate ? new Date(formData.insuranceEndDate) : undefined}
                    popperClassName="z-[100]"
                    wrapperClassName="w-full"
                />
                </div>

                {/* <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                </label>

                <DatePicker
                    selected={formData.insuranceEndDate ? new Date(formData.insuranceEndDate) : null}
                    onChange={(date: Date | null) => {
                    handleInputChange({
                        target: {
                        name: 'insuranceEndDate',
                        value: date ? date.toISOString().split('T')[0] : ''
                        }
                    } as React.ChangeEvent<HTMLInputElement>);
                    }}
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select end date"
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={15}
                    minDate={formData.insuranceStartDate ? new Date(formData.insuranceStartDate) : undefined}
                    popperClassName="z-[100]"
                    wrapperClassName="w-full"
                />
                </div> */}
        </div>
        <div className="flex flex-col-reverse md:flex-row justify-end space-y-reverse space-y-2 md:space-y-0 md:space-x-4 mt-6">
            <button type="button" onClick={onCancel} className="bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 flex items-center justify-center">
                <X className="w-4 h-4 mr-2" />Cancel
            </button>
            <button type="submit" className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 flex items-center justify-center" disabled={isFormSubmitted}>
                {isFormSubmitted ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {isFormSubmitted ? 'Saving...' : editingId ? 'Update Insurance' : 'Save Insurance'}
            </button>
        </div>
      </form>
    </div>
  );
};

export default InsuranceForm;